import React, { useEffect, useState } from 'react';
import { Layout } from '../Layout';
import SpaceBetween from '@cloudscape-design/components/space-between';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import Form from '@cloudscape-design/components/form';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Select from '@cloudscape-design/components/select';
import { OptionDefinition } from '@cloudscape-design/components/internal/components/option/interfaces';
import Input from '@cloudscape-design/components/input';
import { eventsList, gradesList } from 'src/utils/constant';
import { getApiHeader } from 'src/utils';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import Alert from '@cloudscape-design/components/alert';
import { Badge, Box, Modal } from '@cloudscape-design/components';
import Table from '@cloudscape-design/components/table';
import Header from '@cloudscape-design/components/header';
import './style.css';
import { getUserRole, USER_ROLES } from 'src/utils/auth';

export interface EventData {
    EventID: string;
    Grade: string;
    eventCategory: string;
    stageID: string;
    stageName: string;
    status: string;
}

interface AuditRecord {
    Festival_Day: string;
    EventID: number;
    EventName: string;
    EventAgeGrade: string;
    Event_Status: string;
    Data_Consistency: string;
    Admin_Instruction: string;
    CheckedIn_But_Not_Scored_List: string;
    Ghost_Score_List: string;
    Reg_But_Not_Checked_In_List: string;
    CategoryName: string;
    StageName: string;
    Registered_Count: number;
    Checkin_Count: number;
    Scored_Count: number;
    EventPlannedStartDatetime: string;
    EventActualStartDatetime: string;
    EventActualEndDatetime: string;
}

interface AuditResponse {
    eventName: string;
    eventAgeGrade: string;
    totalInconsistentRecords: number;
    queryTimestamp: string;
    status: string;
    message: string;
    metadata: {
        inconsistencyTypes: string[];
        totalRegistered: number;
        totalCheckedIn: number;
        totalScored: number;
        dataIntegrityScore: number;
    };
    auditRecords: AuditRecord[];
}

interface ScoreAuditRecord {
    EventName: string;
    EventAgeGrade: string;
    ParticipantID: number;
    Participant_Name: string;
    ChestNum: string;
    Judge_Name: string;
    CriteriaName: string;
    Max_Limit: number;
    Assigned_Score: number;
    Peer_Avg_Score: number;
    Integrity_Status: string;
    Point_Variance: number;
}

interface ScoreAuditResponse {
    status: string;
    message: string;
    totalRecords?: number;
    scoreAuditRecords?: ScoreAuditRecord[];
    auditRecords?: ScoreAuditRecord[];
}

export const EventStatus: React.FC = () => {

    const [selectedEvent, setSelectedEvent] = useState<OptionDefinition>({ label: 'Select event', value: '' });
    const [selectedGrade, setSelectedGrade] = useState<OptionDefinition>({ label: 'Select grade', value: '' });
    const [eventStatus, setEventStatus] = useState<OptionDefinition>({ label: 'Select status', value: '' });
    const [selectedEventCategory, setSelectedEventCategory] = useState('');
    const [validationError, setValidationError] = useState('');
    const [enableGetEventStatus, setEnableGetEventStatus] = useState(false);
    const [eventStatusModal, setEventStatusModalVisible] = useState<boolean>(false);
    const [auditData, setAuditData] = useState<AuditResponse | null>(null);
    const [showAuditResults, setShowAuditResults] = useState(false);
    const [scoreAuditData, setScoreAuditData] = useState<ScoreAuditResponse | null>(null);
    const [showScoreAuditResults, setShowScoreAuditResults] = useState(false);
    const [discrepancyWarningModal, setDiscrepancyWarningModalVisible] = useState<boolean>(false);

    const headers = getApiHeader();

    const { data: eventStatusData, isLoading, refetch, fetchStatus } = useQuery({
        queryKey: [`getEventStatus-${selectedEventCategory}-${selectedGrade.value}`],
        queryFn: async () => {
            const result = await axios.post('https://u3ea0uwyt4.execute-api.us-east-1.amazonaws.com/Prod/eventstatus',
                {
                    eventCategory: selectedEventCategory,
                    Grade: selectedGrade.value,
                },
                {
                    headers
                });
            return result?.data?.result?.[0] as EventData;
        },
        enabled: enableGetEventStatus,
        staleTime: 0,
    });

    const { mutate: updateEventStatus, data, error: updateEventError, isSuccess, isPending: updateEventPending, reset: resetMutation } = useMutation({
        mutationFn: (data?: { eventName: string; eventAgeGrade: string; endpoint: string }) => {
            return axios.post(data?.endpoint!,
                data, {
                headers
            });
        }
    });

    const { mutate: auditEvent, data: auditResponse, error: auditError, isSuccess: auditSuccess, isPending: auditPending } = useMutation({
        mutationFn: async (data: { eventName: string; eventAgeGrade: string }) => {
            const result = await axios.get(
                `https://wzqvsaoerf.execute-api.us-east-1.amazonaws.com/Prod/event/audit?eventName=${encodeURIComponent(data.eventName)}&eventAgeGrade=${encodeURIComponent(data.eventAgeGrade)}`,
                { headers }
            );
            return result.data as AuditResponse;
        }
    });

    const { mutate: auditScore, data: scoreAuditResponse, error: scoreAuditError, isSuccess: scoreAuditSuccess, isPending: scoreAuditPending } = useMutation({
        mutationFn: async (data: { eventName: string; eventAgeGrade: string }) => {
            const result = await axios.get(
                `https://dm3y4im64d.execute-api.us-east-1.amazonaws.com/Prod/score/audit?eventName=${encodeURIComponent(data.eventName)}&eventAgeGrade=${encodeURIComponent(data.eventAgeGrade)}`,
                { headers }
            );
            return result.data as ScoreAuditResponse;
        }
    });

    const searchEvent = () => {
        if (!selectedEvent.value || !selectedGrade.value || !selectedEventCategory) {
            setValidationError('Please select event category and grade to continue');
            setEnableGetEventStatus(false);
            setShowAuditResults(false);
            setShowScoreAuditResults(false);
        } else {
            setEnableGetEventStatus(true);
            setValidationError('');
        }
    }

    // Call audit APIs only when event status data is loaded and status is "Started"
    useEffect(() => {
        if (eventStatusData && eventStatusData.status === 'Started') {
            // Call audit APIs when event is started
            auditEvent({
                eventName: selectedEventCategory,
                eventAgeGrade: selectedGrade.value!
            });
            auditScore({
                eventName: selectedEventCategory,
                eventAgeGrade: selectedGrade.value!
            });
        } else if (eventStatusData) {
            // Clear audit data if event is not started
            setShowAuditResults(false);
            setShowScoreAuditResults(false);
            setAuditData(null);
            setScoreAuditData(null);
        }
    }, [eventStatusData]);

    const updateEvent = () => {
        console.log('updateEvent called with eventStatus:', eventStatus.value);
        if (!eventStatus.value) {
            setValidationError('Please select event status');
        } else {
            setValidationError('');
            
            // Only check for discrepancies when completing an event (not when starting)
            if (eventStatus.value === 'Completed') {
                console.log('Checking discrepancies for Completed status');
                const hasEventAuditDiscrepancies = auditData && auditData.totalInconsistentRecords > 0;
                const scoreDiscrepancies = (scoreAuditData?.scoreAuditRecords || scoreAuditData?.auditRecords || []).filter(
                    record => record.Point_Variance !== 0
                );
                const hasScoreDiscrepancies = scoreDiscrepancies.length > 0;
                
                // If there are discrepancies, show warning modal
                if (hasEventAuditDiscrepancies || hasScoreDiscrepancies) {
                    setDiscrepancyWarningModalVisible(true);
                    return;
                }
            }
            
            // Proceed with update
            console.log('Proceeding with update for status:', eventStatus.value);
            proceedWithUpdate();
        }
    }

    const proceedWithUpdate = () => {
        console.log('proceedWithUpdate called');
        let endpoint = 'https://v8nlc1qtnc.execute-api.us-east-1.amazonaws.com/Prod/event/start ';
        if (eventStatus.value === 'Completed') {
            console.log('Opening modal for Completed status');
            setEventStatusModalVisible(true);
            return;
        }
        console.log('Calling updateEventStatus with endpoint:', endpoint, 'eventName:', selectedEventCategory, 'eventAgeGrade:', selectedGrade.value);
        updateEventStatus({
            endpoint,
            eventName: selectedEventCategory,
            eventAgeGrade: selectedGrade.value!
        });
    }

    useEffect(() => {
        if (isSuccess) {
            refetch();
        }
    }, [isSuccess]);

    useEffect(() => {
        if (auditSuccess && auditResponse) {
            setAuditData(auditResponse);
            setShowAuditResults(true);
        }
    }, [auditSuccess, auditResponse]);

    useEffect(() => {
        if (scoreAuditSuccess && scoreAuditResponse) {
            setScoreAuditData(scoreAuditResponse);
            setShowScoreAuditResults(true);
        }
    }, [scoreAuditSuccess, scoreAuditResponse]);

    const refreshAudit = () => {
        if (selectedEventCategory && selectedGrade.value) {
            auditEvent({
                eventName: selectedEventCategory,
                eventAgeGrade: selectedGrade.value!
            });
        }
    };

    const refreshScoreAudit = () => {
        if (selectedEventCategory && selectedGrade.value) {
            auditScore({
                eventName: selectedEventCategory,
                eventAgeGrade: selectedGrade.value!
            });
        }
    };

    const reset = () => {
        setSelectedEvent({ label: 'Select event', value: '' });
        setSelectedGrade({ label: 'Select grade', value: '' });
        setEventStatus({ label: 'Select status', value: '' });
        setEnableGetEventStatus(false);
        setShowAuditResults(false);
        setShowScoreAuditResults(false);
        setAuditData(null);
        setScoreAuditData(null);
        resetMutation();
    };

    return (
        <Layout breadcrumbs={[
            {
                href: '/eventstatus',
                text: 'Event status'
            }
        ]}>
            <SpaceBetween direction="vertical" size="l">
                {
                    (validationError || updateEventError || auditError || scoreAuditError) && (
                        <Alert type='error'>{validationError || updateEventError?.message || auditError?.message || scoreAuditError?.message}</Alert>
                    )
                }
                <ExpandableSection
                    defaultExpanded
                    variant="container"
                    headerText="Search event"
                >
                    <Form
                        actions={
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button formAction="none" variant="link" onClick={reset}>
                                    Clear
                                </Button>
                                <Button variant="primary" onClick={searchEvent} loading={isLoading || auditPending || scoreAuditPending}>Search</Button>
                            </SpaceBetween>
                        }
                    >
                        <SpaceBetween direction="horizontal" size="l">
                            <FormField label="Event category">
                                <Select
                                    selectedOption={selectedEvent}
                                    onChange={({ detail }) => {
                                        setSelectedEvent(detail.selectedOption);
                                        setSelectedEventCategory(detail.selectedOption.label!);
                                        setValidationError('');
                                        setEnableGetEventStatus(false);
                                        setShowAuditResults(false);
                                        setShowScoreAuditResults(false);
                                        setAuditData(null);
                                        setScoreAuditData(null);
                                        resetMutation();
                                    }}
                                    options={eventsList.map((event) => ({
                                        label: event.name, value: event.id
                                    }))}
                                />
                            </FormField>
                            <FormField label="Grade">
                                <Select
                                    selectedOption={selectedGrade}
                                    onChange={({ detail }) => {
                                        setSelectedGrade(detail.selectedOption);
                                        setValidationError('');
                                        setEnableGetEventStatus(false);
                                        setShowAuditResults(false);
                                        setShowScoreAuditResults(false);
                                        setAuditData(null);
                                        setScoreAuditData(null);
                                        resetMutation();
                                    }
                                    }
                                    options={gradesList.map((grade) => ({
                                        label: grade, value: grade
                                    }))} />
                            </FormField>
                        </SpaceBetween>
                    </Form>
                </ExpandableSection>
                {
                    eventStatusData && (
                        <ExpandableSection
                            defaultExpanded
                            variant="container"
                            headerText="Update event status"
                        >
                            <SpaceBetween direction="vertical" size="l">
                                {
                                    isSuccess && (
                                        <Alert type='success'>Event status updated successfully</Alert>
                                    )
                                }
                                <Form
                                    actions={
                                        <SpaceBetween direction="horizontal" size="xs">
                                            <Button variant="primary" onClick={updateEvent} loading={updateEventPending}>Update event status</Button>
                                        </SpaceBetween>
                                    }
                                >
                                    <SpaceBetween direction="horizontal" size="l">
                                        <FormField label="Event category">
                                            <Input disabled value={eventStatusData.eventCategory} placeholder='Event category' />
                                        </FormField>
                                        <FormField label="Grade">
                                            <Input disabled value={eventStatusData.Grade} placeholder='Grade' />
                                        </FormField>
                                        <FormField label="Stage">
                                            <Input disabled value={eventStatusData.stageName} placeholder='Stage' />
                                        </FormField>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                            <div style={{ 
                                                color: '#ffffff',
                                                fontSize: '14px',
                                                fontWeight: 400,
                                                lineHeight: '20px',
                                                marginBottom: '4px'
                                            }}>
                                                Status
                                            </div>
                                            <div>
                                                {
                                                    eventStatusData.status === 'Not Started' && (
                                                        <Badge color='red'>{eventStatusData.status}</Badge>
                                                    )
                                                }
                                                {
                                                    eventStatusData.status === 'Started' && (
                                                        <Badge color='blue'>{eventStatusData.status}</Badge>
                                                    )
                                                }
                                                {
                                                    eventStatusData.status === 'Completed' && (
                                                        <Badge color='green'>{eventStatusData.status}</Badge>
                                                    )
                                                }
                                            </div>
                                        </div>
                                        <FormField label="Select event status to update">
                                            <Select
                                                selectedOption={eventStatus}
                                                onChange={({ detail }) =>
                                                    setEventStatus(detail.selectedOption)
                                                }
                                                options={[
                                                    { label: "Started", value: "Started" },
                                                    { label: "Completed", value: "Completed", disabled: getUserRole() !== USER_ROLES.SUPER_ADMIN },
                                                ]}
                                            />
                                        </FormField>
                                    </SpaceBetween>
                                </Form>
                            </SpaceBetween>
                        </ExpandableSection>
                    )
                }
                {
                    eventStatusData && eventStatusData.status !== 'Started' && (
                        <ExpandableSection
                            defaultExpanded
                            variant="container"
                            headerText="Event Audit Results"
                        >
                            <SpaceBetween direction="vertical" size="l">
                                {
                                    eventStatusData.status === 'Not Started' ? (
                                        <Alert type="info" header="Event Not Started">
                                            This event has not been started yet. Please start the event to view audit results.
                                        </Alert>
                                    ) : eventStatusData.status === 'Completed' ? (
                                        <Alert type="info" header="Event Completed">
                                            This event has already been completed.
                                        </Alert>
                                    ) : null
                                }
                            </SpaceBetween>
                        </ExpandableSection>
                    )
                }
                {
                    showAuditResults && auditData && eventStatusData && eventStatusData.status === 'Started' && (
                        <ExpandableSection
                            defaultExpanded
                            variant="container"
                            headerText="Event Audit Results"
                        >
                            <SpaceBetween direction="vertical" size="l">
                                {
                                    auditData.totalInconsistentRecords < 1 ? (
                                        <Alert type="success" header="Event Ready for Closure">
                                            All looks good, we can close the event.
                                        </Alert>
                                    ) : (
                                        <Alert type="warning" header={`Found ${auditData.totalInconsistentRecords} inconsistency issue(s)`}>
                                            {auditData.message}
                                        </Alert>
                                    )
                                }
                                {auditData.metadata && (
                                    <Box>
                                        <SpaceBetween direction="horizontal" size="s">
                                            <Badge color="blue">Total Registered: {auditData.metadata.totalRegistered}</Badge>
                                            <Badge color="green">Total Checked In: {auditData.metadata.totalCheckedIn}</Badge>
                                            <Badge color="blue">Total Scored: {auditData.metadata.totalScored}</Badge>
                                            <Badge color={auditData.metadata.dataIntegrityScore < 100 ? "red" : "green"}>Data Integrity Score: {auditData.metadata.dataIntegrityScore}%</Badge>
                                        </SpaceBetween>
                                    </Box>
                                )}
                                {
                                    auditData.totalInconsistentRecords > 0 && (
                                        <>
                                            <Table
                                                columnDefinitions={[
                                                    {
                                                        id: "festivalDay",
                                                        header: "Festival Day",
                                                        cell: item => item.Festival_Day || "-",
                                                    },
                                                    {
                                                        id: "eventId",
                                                        header: "Event ID",
                                                        cell: item => item.EventID || "-",
                                                    },
                                                    {
                                                        id: "eventName",
                                                        header: "Event Name",
                                                        cell: item => item.EventName || "-",
                                                    },
                                                    {
                                                        id: "eventAgeGrade",
                                                        header: "Age Grade",
                                                        cell: item => item.EventAgeGrade || "-"
                                                    },
                                                    {
                                                        id: "eventStatus",
                                                        header: "Event Status",
                                                        cell: item => (
                                                            <Badge color={item.Event_Status === 'Completed' ? 'green' : 'blue'}>
                                                                {item.Event_Status}
                                                            </Badge>
                                                        )
                                                    },
                                                    {
                                                        id: "stageName",
                                                        header: "Stage",
                                                        cell: item => item.StageName || "-"
                                                    },
                                                    {
                                                        id: "registeredCount",
                                                        header: "Registered",
                                                        cell: item => item.Registered_Count || 0
                                                    },
                                                    {
                                                        id: "checkinCount",
                                                        header: "Checked In",
                                                        cell: item => item.Checkin_Count || 0
                                                    },
                                                    {
                                                        id: "scoredCount",
                                                        header: "Scored",
                                                        cell: item => item.Scored_Count || 0
                                                    },
                                                    {
                                                        id: "dataConsistency",
                                                        header: "Data Consistency",
                                                        cell: item => (
                                                            <Badge color={item.Data_Consistency.includes('MISMATCH') ? 'red' : 'green'}>
                                                                {item.Data_Consistency}
                                                            </Badge>
                                                        )
                                                    },
                                                    {
                                                        id: "missingScores",
                                                        header: "Missing Scores",
                                                        cell: item => item.CheckedIn_But_Not_Scored_List || "-"
                                                    },
                                                    {
                                                        id: "ghostScores",
                                                        header: "Ghost Scores",
                                                        cell: item => item.Ghost_Score_List || "-"
                                                    },
                                                    {
                                                        id: "notCheckedIn",
                                                        header: "Not Checked In",
                                                        cell: item => item.Reg_But_Not_Checked_In_List || "-"
                                                    },
                                                    {
                                                        id: "adminInstruction",
                                                        header: "Admin Instruction",
                                                        cell: item => item.Admin_Instruction || "-"
                                                    }
                                                ]}
                                                items={auditData.auditRecords}
                                                loadingText="Loading audit results"
                                                sortingDisabled
                                                header={
                                                    <Header 
                                                        actions={
                                                            auditData.totalInconsistentRecords > 0 ? (
                                                                <Button 
                                                                    variant="normal" 
                                                                    onClick={refreshAudit}
                                                                    loading={auditPending}
                                                                    iconName="refresh"
                                                                >
                                                                    Refresh
                                                                </Button>
                                                            ) : undefined
                                                        }
                                                    >
                                                        Audit Records ({auditData.auditRecords.length})
                                                    </Header>
                                                }
                                                empty={
                                                    <Box
                                                        margin={{ vertical: "xs" }}
                                                        textAlign="center"
                                                        color="inherit"
                                                    >
                                                        <SpaceBetween size="m">
                                                            <b>No audit records found</b>
                                                        </SpaceBetween>
                                                    </Box>
                                                }
                                            />
                                        </>
                                    )
                                }
                            </SpaceBetween>
                        </ExpandableSection>
                    )
                }
                {
                    eventStatusData && eventStatusData.status !== 'Started' && (
                        <ExpandableSection
                            defaultExpanded
                            variant="container"
                            headerText="Score Audit Results"
                        >
                            <SpaceBetween direction="vertical" size="l">
                                {
                                    eventStatusData.status === 'Not Started' ? (
                                        <Alert type="info" header="Event Not Started">
                                            This event has not been started yet. Please start the event to view score audit results.
                                        </Alert>
                                    ) : eventStatusData.status === 'Completed' ? (
                                        <Alert type="info" header="Event Completed">
                                            This event has already been completed.
                                        </Alert>
                                    ) : null
                                }
                            </SpaceBetween>
                        </ExpandableSection>
                    )
                }
                {
                    showScoreAuditResults && scoreAuditData && eventStatusData && eventStatusData.status === 'Started' && (
                        <ExpandableSection
                            defaultExpanded
                            variant="container"
                            headerText="Score Audit Results"
                        >
                            <SpaceBetween direction="vertical" size="l">
                                <Alert type="info" header="Score Audit">
                                    {scoreAuditData.message || 'Score audit results'}
                                </Alert>
                                <Box>
                                    <SpaceBetween direction="horizontal" size="s">
                                        <Badge color="blue">
                                            Total Records: {(scoreAuditData.scoreAuditRecords?.length || scoreAuditData.auditRecords?.length || 0)}
                                        </Badge>
                                        {(() => {
                                            const discrepancies = (scoreAuditData.scoreAuditRecords || scoreAuditData.auditRecords || []).filter(
                                                record => record.Point_Variance !== 0
                                            ).length;
                                            return (
                                                <Badge color={discrepancies > 0 ? "red" : "green"}>
                                                    Discrepancies: {discrepancies}
                                                </Badge>
                                            );
                                        })()}
                                    </SpaceBetween>
                                </Box>
                                <div className="score-audit-table-container">
                                    <div className="score-audit-table">
                                        <Table
                                        columnDefinitions={[
                                            {
                                                id: "eventName",
                                                header: "Event Name",
                                                cell: item => (
                                                    <div className={item.Point_Variance !== 0 ? 'variance-row' : ''}>
                                                        {item.EventName || "-"}
                                                    </div>
                                                ),
                                            },
                                            {
                                                id: "eventAgeGrade",
                                                header: "Age Grade",
                                                cell: item => (
                                                    <div className={item.Point_Variance !== 0 ? 'variance-row' : ''}>
                                                        {item.EventAgeGrade || "-"}
                                                    </div>
                                                )
                                            },
                                            {
                                                id: "participantId",
                                                header: "Participant ID",
                                                cell: item => (
                                                    <div className={item.Point_Variance !== 0 ? 'variance-row' : ''}>
                                                        {item.ParticipantID || "-"}
                                                    </div>
                                                ),
                                            },
                                            {
                                                id: "participantName",
                                                header: "Participant Name",
                                                cell: item => (
                                                    <div className={item.Point_Variance !== 0 ? 'variance-row' : ''}>
                                                        {item.Participant_Name || "-"}
                                                    </div>
                                                ),
                                            },
                                            {
                                                id: "chestNum",
                                                header: "Chest #",
                                                cell: item => (
                                                    <div className={item.Point_Variance !== 0 ? 'variance-row' : ''}>
                                                        {item.ChestNum || "-"}
                                                    </div>
                                                ),
                                            },
                                            {
                                                id: "judgeName",
                                                header: "Judge Name",
                                                cell: item => (
                                                    <div className={item.Point_Variance !== 0 ? 'variance-row' : ''}>
                                                        {item.Judge_Name || "-"}
                                                    </div>
                                                ),
                                            },
                                            {
                                                id: "criteriaName",
                                                header: "Criteria",
                                                cell: item => (
                                                    <div className={item.Point_Variance !== 0 ? 'variance-row' : ''}>
                                                        {item.CriteriaName || "-"}
                                                    </div>
                                                ),
                                            },
                                            {
                                                id: "maxLimit",
                                                header: "Max Limit",
                                                cell: item => (
                                                    <div className={item.Point_Variance !== 0 ? 'variance-row' : ''}>
                                                        {item.Max_Limit || 0}
                                                    </div>
                                                ),
                                            },
                                            {
                                                id: "assignedScore",
                                                header: "Assigned Score",
                                                cell: item => (
                                                    <div className={item.Point_Variance !== 0 ? 'variance-row' : ''}>
                                                        {item.Assigned_Score || 0}
                                                    </div>
                                                ),
                                            },
                                            {
                                                id: "peerAvgScore",
                                                header: "Peer Avg Score",
                                                cell: item => (
                                                    <div className={item.Point_Variance !== 0 ? 'variance-row' : ''}>
                                                        {item.Peer_Avg_Score?.toFixed(2) || "0.00"}
                                                    </div>
                                                ),
                                            },
                                            {
                                                id: "integrityStatus",
                                                header: "Integrity Status",
                                                cell: item => (
                                                    <div className={item.Point_Variance !== 0 ? 'variance-row' : ''}>
                                                        <span style={{ 
                                                            color: item.Integrity_Status === 'OK' ? 'green' : 'red',
                                                            fontWeight: 500,
                                                            whiteSpace: 'nowrap'
                                                        }}>
                                                            {item.Integrity_Status}
                                                        </span>
                                                    </div>
                                                )
                                            },
                                            {
                                                id: "pointVariance",
                                                header: "Point Variance",
                                                cell: item => (
                                                    <div className={item.Point_Variance !== 0 ? 'variance-row' : ''}>
                                                        <span style={{ fontWeight: item.Point_Variance !== 0 ? 'bold' : 'normal' }}>
                                                            {item.Point_Variance?.toFixed(2) || "0.00"}
                                                        </span>
                                                    </div>
                                                )
                                            }
                                        ]}
                                        items={scoreAuditData.scoreAuditRecords || scoreAuditData.auditRecords || []}
                                        loadingText="Loading score audit results"
                                        sortingDisabled
                                        stickyHeader
                                        variant="container"
                                        trackBy="ParticipantID"
                                        header={
                                            <Header 
                                                actions={
                                                    <Button 
                                                        variant="normal" 
                                                        onClick={refreshScoreAudit}
                                                        loading={scoreAuditPending}
                                                        iconName="refresh"
                                                    >
                                                        Refresh
                                                    </Button>
                                                }
                                            >
                                                Score Audit Records
                                            </Header>
                                        }
                                        empty={
                                            <Box
                                                margin={{ vertical: "xs" }}
                                                textAlign="center"
                                                color="inherit"
                                            >
                                                <SpaceBetween size="m">
                                                    <b>No score audit records found</b>
                                                </SpaceBetween>
                                            </Box>
                                        }
                                    />
                                    </div>
                                </div>
                            </SpaceBetween>
                        </ExpandableSection>
                    )
                }
                <Modal
                    onDismiss={() => setDiscrepancyWarningModalVisible(false)}
                    visible={discrepancyWarningModal}
                    footer={
                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button variant="link" onClick={() => setDiscrepancyWarningModalVisible(false)}>Cancel</Button>
                                <Button variant="primary" onClick={() => {
                                    setDiscrepancyWarningModalVisible(false);
                                    proceedWithUpdate();
                                }}>Yes, Update Anyway</Button>
                            </SpaceBetween>
                        </Box>
                    }
                    header="Discrepancies Detected"
                >
                    <SpaceBetween direction="vertical" size="s">
                        <Alert type="warning">
                            There are discrepancies in the audit results. Are you sure you want to update the event status?
                        </Alert>
                        {auditData && auditData.totalInconsistentRecords > 0 && (
                            <Box>
                                <strong>Event Audit:</strong> {auditData.totalInconsistentRecords} inconsistenc{auditData.totalInconsistentRecords === 1 ? 'y' : 'ies'} found
                            </Box>
                        )}
                        {(() => {
                            const scoreDiscrepancies = (scoreAuditData?.scoreAuditRecords || scoreAuditData?.auditRecords || []).filter(
                                record => record.Point_Variance !== 0
                            ).length;
                            return scoreDiscrepancies > 0 ? (
                                <Box>
                                    <strong>Score Audit:</strong> {scoreDiscrepancies} discrepanc{scoreDiscrepancies === 1 ? 'y' : 'ies'} found
                                </Box>
                            ) : null;
                        })()}
                    </SpaceBetween>
                </Modal>
                <Modal
                    onDismiss={() => setEventStatusModalVisible(false)}
                    visible={eventStatusModal}
                    footer={
                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button variant="link" onClick={() => setEventStatusModalVisible(false)}>Cancel</Button>
                                <Button variant="primary" onClick={() => {
                                    updateEventStatus({
                                        endpoint: 'https://3py0j5te4b.execute-api.us-east-1.amazonaws.com/Prod/event/end',
                                        eventName: selectedEventCategory,
                                        eventAgeGrade: selectedGrade.value!
                                    });
                                    setEventStatusModalVisible(false);
                                }}>Update</Button>
                            </SpaceBetween>
                        </Box>
                    }
                    header="Event status"
                >
                    Are you sure you want to mark the event as completed?
                </Modal>
            </SpaceBetween>
        </Layout>
    );
}